import { User, Shield, Key, Globe, LogOut, CheckCircle2, UserCog, ExternalLink } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';

export function AccountPage() {
  const { session, logout, switchAccount } = useAuth();
  const principal = session?.principal;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)', maxWidth: 760 }}>
      <div>
        <h1 style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--fw-bold)' }}>Personal Account</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginTop: 4 }}>
          Identity claims and global authentication session managed via CYRN SSO.
        </p>
      </div>

      <Card padding="var(--space-6)">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 'var(--radius-full)',
                background: 'var(--navy-700)',
                border: '2px solid var(--accent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--accent)',
                fontWeight: 700,
                fontSize: 'var(--text-lg)',
              }}
            >
              {principal?.display_name?.charAt(0).toUpperCase() || principal?.email?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--fw-semibold)' }}>
                  {principal?.display_name || 'Authenticated User'}
                </h2>
                <Badge tone="success" dot>
                  {principal?.status || 'active'}
                </Badge>
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginTop: 2 }}>
                {principal?.email || 'No email attached'}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Button variant="secondary" size="sm" onClick={switchAccount} icon={<UserCog size={14} />}>
              Switch Account
            </Button>
            <Button variant="danger" size="sm" onClick={logout} icon={<LogOut size={14} />}>
              Sign Out
            </Button>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 'var(--text-sm)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border-subtle)', paddingBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-tertiary)' }}>
              <Key size={14} />
              <span>CYRN Auth Immutable Subject (sub):</span>
            </div>
            <code style={{ color: 'var(--accent)', fontSize: 'var(--text-xs)' }}>{principal?.subject || 'N/A'}</code>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border-subtle)', paddingBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-tertiary)' }}>
              <User size={14} />
              <span>Nexus Principal UUID:</span>
            </div>
            <code style={{ fontSize: 'var(--text-xs)' }}>{principal?.id || 'N/A'}</code>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border-subtle)', paddingBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-tertiary)' }}>
              <Globe size={14} />
              <span>Preferred Locale:</span>
            </div>
            <Badge tone="neutral">{principal?.locale?.toUpperCase() || 'FR'}</Badge>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-tertiary)' }}>
              <Shield size={14} />
              <span>Single Sign-On Authority:</span>
            </div>
            <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>CYRN Auth (OIDC Federation)</span>
          </div>
        </div>
      </Card>

      {/* Session management */}
      <Card padding="var(--space-5)">
        <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--fw-semibold)', marginBottom: 6 }}>
          Session &amp; identity
        </h3>
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 16 }}>
          <strong style={{ color: 'var(--text-primary)' }}>Switch account</strong> keeps your CYRN SSO session and
          opens the account chooser, so you can continue as a different identity.{' '}
          <strong style={{ color: 'var(--text-primary)' }}>Sign out</strong> ends the Nexus session and terminates the
          CYRN SSO session across every CYRN service.
        </p>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Button variant="secondary" size="sm" onClick={switchAccount} icon={<UserCog size={14} />}>
            Switch account
          </Button>
          <a
            href="https://auth.cyrn.fr/ui/console/users/me"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 'var(--text-xs)',
              color: 'var(--accent)',
              textDecoration: 'none',
              fontWeight: 'var(--fw-medium)',
              padding: '6px 12px',
            }}
          >
            Manage password &amp; MFA in CYRN Auth <ExternalLink size={13} />
          </a>
        </div>
      </Card>

      {/* Tenancy & Security rules card */}
      <Card padding="var(--space-5)" style={{ background: 'var(--bg-inset)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <CheckCircle2 size={18} color="var(--accent)" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            <strong style={{ color: 'var(--text-primary)' }}>Decoupled Identity Model (ADR-0002 / SPEC-01):</strong><br />
            Nexus never stores passwords. Passwords, passkeys and multi-factor authentication (MFA/2FA) are strictly guarded inside CYRN Auth. To update your password or authentication factors, visit the CYRN Auth console.
          </div>
        </div>
      </Card>
    </div>
  );
}
